import * as React from 'react'
// libs
import styled, { css } from 'styled-components'
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client/react'
import { DropzoneRef, useDropzone } from 'react-dropzone'
// hooks
import { useFileUpload } from 'hooks/useFileUpload'
// graphql
import { CREATE_ACTIVITY_MUTATION } from 'graphql/activities/mutations'
// constants
import { ALLOWED_MIME_TYPES } from 'utils/validators'
// types
import {
  ILog,
  IPlan,
  IPlanKpi,
  AssetType,
  IPlanAction,
  IPlanObjective,
  LogActionTypes,
  LogEntityTypes
} from 'types/graphql'
// themes
import { colors } from 'themes'
// context
import { ActivitiesListAPIContext } from 'containers/Activities/layers/API/ActivitiesList'
// utils
import { isFunction } from 'lodash/fp'
import { handleError } from 'utils/errors/handlers'

// styled
const DropzoneWrapper = styled('div')<{ $isTreeElement: boolean }>`
  height: ${({ $isTreeElement }) => ($isTreeElement ? '100%' : 'unset')};
  min-height: 100%;
  outline: none;
  position: relative;
  width: 100%;
`

const CoverContainer = styled('div')<ICoverContainerProps>`
  align-items: center;
  background-color: rgb(255, 255, 255);
  color: ${colors.black};
  display: flex;
  height: 100%;
  justify-content: center;
  opacity: 0.9;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 9999999;

  > span {
    font-weight: 500;
    position: ${({ isTreeElement }) => (isTreeElement ? 'absolute' : 'fixed')};
    top: 50%;
    transform: translateY(-50%);
  }

  ${({ isTreeElement }) =>
    isTreeElement &&
    css`
      height: 44px;
    `};

  ${({ isTreeElement, isObjective }) =>
    isTreeElement &&
    isObjective &&
    css`
      height: 46px;
      top: 0;
    `};
`

// types
interface ICoverContainerProps {
  isObjective: boolean
  isTreeElement: boolean
}

interface IProps {
  open?: DropzoneRef
  entity: ILog & IPlan & IPlanAction & IPlanKpi & IPlanObjective
  refetch?: any
  disableDrag?: boolean
  isObjective?: boolean
  disableCover?: boolean
  isTreeElement?: boolean
  isRootObjective?: boolean
}

const withDropzone = (EnhancedComponent: React.FC<any>) => {
  const ComponentWithDropzone: React.FC<IProps> = (props) => {
    const { entity, refetch, disableDrag = false, isObjective, disableCover, isTreeElement, isRootObjective } = props

    const [uploadImageMutation] = useMutation(CREATE_ACTIVITY_MUTATION)

    const { upload } = useFileUpload()
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()

    const { beforeUpload } = React.useContext(ActivitiesListAPIContext)

    const onDrop = React.useCallback(([file]) => {
      if (!file) {
        return
      }

      const reader = new FileReader()

      reader.onloadstart = () =>
        enqueueSnackbar('Uploading...', {
          key: 'upload',
          variant: 'progress',
          autoHideDuration: null
        })
      reader.onloadend = () => handleUploadImage(file)

      reader.readAsDataURL(file)
    }, [])

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      onDrop,
      noClick: true,
      multiple: false,
      noDrag: disableDrag
    })

    const handleUploadImage = async (file) => {
      let entityToUpload = entity

      if (isFunction(beforeUpload)) {
        entityToUpload = await beforeUpload()
      }

      try {
        const downloadUrl = await upload(file, {
          file: {
            assetType: AssetType.ActivityFile,
            contentType: file.type,
            entityPrefix: `entity/${entityToUpload.id}/assets`,
            originalFileName: file.name
          }
        })

        await uploadImageMutation({
          variables: {
            input: {
              entityId: entityToUpload.id,
              entityType: LogEntityTypes.Image,
              actionType: LogActionTypes.Create,
              metadata: {
                fileName: file.name,
                fileUrl: downloadUrl,
                fileContentType: file.type
              }
            }
          }
        })

        await refetch?.()

        closeSnackbar('upload')
      } catch (error) {
        handleError(error, 'Dropzone.handleUploadImage')
      }
    }

    const shouldShowCover = isDragActive && !disableDrag && !disableCover

    return (
      <DropzoneWrapper {...getRootProps()} $isTreeElement={isTreeElement}>
        <input {...getInputProps()} accept={ALLOWED_MIME_TYPES.join(',')} />
        {shouldShowCover && (
          <CoverContainer isTreeElement={isTreeElement} isObjective={isObjective || isRootObjective}>
            <span>Drop files to upload</span>
          </CoverContainer>
        )}
        <EnhancedComponent open={open} {...props} />
      </DropzoneWrapper>
    )
  }

  return ComponentWithDropzone
}

export default withDropzone
